/* eslint-disable react/prop-types */
import React, { useMemo } from "react";

import { Card, Col, Row, Spin, Statistic, Typography } from "antd";

import {
  BookOutlined,
  DollarOutlined,
  MessageOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  formatIndianCurrencyToReadableWords,
  formatNumberToReadable,
} from "../../Utils/formatter";

const { Text } = Typography;

const DashboardCards = ({ dashboardMetrics = {}, loading = false }) => {
  const cardStyle = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };

  const bodyStyle = {
    padding: "12px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  };

  const formattedDashboardMetrics = useMemo(() => {
    const dashboardMetricsWithDefaultValues = {
      totalCoaches: dashboardMetrics.totalCoaches || 0,
      totalAssignedCoaches: dashboardMetrics.totalAssignedCoaches || 0,
      totalEarnings: dashboardMetrics.totalEarnings || 0,
      totalEarnedCoaches: dashboardMetrics.totalEarnedCoaches || 0,
      totalActiveCoaches: dashboardMetrics.totalActiveCoaches || 0,
      totalActiveCoachesEarnings:
        dashboardMetrics.totalActiveCoachesEarnings || 0,
      totalActiveWorkshops: dashboardMetrics.totalActiveWorkshops || 0,
      totalActiveWorkshopCreators:
        dashboardMetrics.totalActiveWorkshopCreators || 0,
      totalActiveOneToOne: dashboardMetrics.totalActiveOneToOne || 0,
      totalActiveOneToOneCreators:
        dashboardMetrics.totalActiveOneToOneCreators || 0,
      totalActiveCourses: dashboardMetrics.totalActiveCourses || 0,
      totalActiveCourseCreators:
        dashboardMetrics.totalActiveCourseCreators || 0,
      totalCustomers: dashboardMetrics.totalCustomers || 0,
      totalFreeCustomers: dashboardMetrics.totalFreeCustomers || 0,
      freedomCoaches: {
        tier1: dashboardMetrics.freedomCoaches.tier1 || 0,
        tier2: dashboardMetrics.freedomCoaches.tier2 || 0,
        tier3: dashboardMetrics.freedomCoaches.tier3 || 0,
        tier4: dashboardMetrics.freedomCoaches.tier4 || 0,
        tier5: dashboardMetrics.freedomCoaches.tier5 || 0,
      },
      enterpriseCoaches: {
        tier1: dashboardMetrics.enterpriseCoaches.tier1 || 0,
        tier2: dashboardMetrics.enterpriseCoaches.tier2 || 0,
        tier3: dashboardMetrics.enterpriseCoaches.tier3 || 0,
        tier4: dashboardMetrics.enterpriseCoaches.tier4 || 0,
        tier5: dashboardMetrics.enterpriseCoaches.tier5 || 0,
      },
    };

    const {
      totalCoaches,
      totalAssignedCoaches,
      totalActiveCoaches,
      totalActiveCoachesEarnings,
      freedomCoaches,
      enterpriseCoaches,
      totalActiveCourses,
      totalActiveCourseCreators,
      totalActiveWorkshops,
      totalActiveWorkshopCreators,
      totalActiveOneToOne,
      totalActiveOneToOneCreators,
      totalEarnings,
      totalEarnedCoaches,
      totalCustomers,
      totalFreeCustomers,
    } = dashboardMetricsWithDefaultValues;

    const totalFreedomCoaches =
      freedomCoaches.tier1 +
      freedomCoaches.tier2 +
      freedomCoaches.tier3 +
      freedomCoaches.tier4;

    const totalEnterpriseCoaches =
      enterpriseCoaches.tier1 +
      enterpriseCoaches.tier2 +
      enterpriseCoaches.tier3 +
      enterpriseCoaches.tier4;

    return {
      // overall coaches
      totalCoaches: formatNumberToReadable(totalCoaches),
      totalAssignedCoaches: formatNumberToReadable(totalAssignedCoaches),
      totalUnassignedCoaches: formatNumberToReadable(
        totalCoaches - totalAssignedCoaches
      ),

      // active coaches
      totalActiveCoaches: formatNumberToReadable(totalActiveCoaches),
      totalInactiveCoaches: formatNumberToReadable(
        totalCoaches - totalActiveCoaches
      ),
      totalActiveCoachesEarnings: formatIndianCurrencyToReadableWords(
        totalActiveCoachesEarnings
      ),

      // setup distribution
      totalFreedomCoaches: formatNumberToReadable(totalFreedomCoaches),
      totalEnterpriseCoaches: formatNumberToReadable(totalEnterpriseCoaches),
      freedomCoaches: {
        tier1: formatNumberToReadable(freedomCoaches.tier1),
        tier2: formatNumberToReadable(freedomCoaches.tier2),
        tier3: formatNumberToReadable(freedomCoaches.tier3),
        tier4: formatNumberToReadable(freedomCoaches.tier4),
        tier5: formatNumberToReadable(freedomCoaches.tier5),
      },
      enterpriseCoaches: {
        tier1: formatNumberToReadable(enterpriseCoaches.tier1),
        tier2: formatNumberToReadable(enterpriseCoaches.tier2),
        tier3: formatNumberToReadable(enterpriseCoaches.tier3),
        tier4: formatNumberToReadable(enterpriseCoaches.tier4),
        tier5: formatNumberToReadable(enterpriseCoaches.tier5),
      },

      // courses
      totalActiveCourses: formatNumberToReadable(totalActiveCourses),
      totalActiveCourseCreators: formatNumberToReadable(
        totalActiveCourseCreators
      ),
      avgActiveCoursesForCreator: formatNumberToReadable(
        (totalActiveCourses / (totalActiveCourseCreators || 1)).toFixed(1)
      ),

      // workshops
      totalActiveWorkshops: formatNumberToReadable(totalActiveWorkshops),
      totalActiveWorkshopCreators: formatNumberToReadable(
        totalActiveWorkshopCreators
      ),
      avgActiveWorkshopsForCreator: formatNumberToReadable(
        (totalActiveWorkshops / (totalActiveWorkshopCreators || 1)).toFixed(1)
      ),

      // one-on-ones
      totalActiveOneToOne: formatNumberToReadable(totalActiveOneToOne),
      totalActiveOneToOneCreators: formatNumberToReadable(
        totalActiveOneToOneCreators
      ),
      avgActiveOneToOneForCreator: formatNumberToReadable(
        (totalActiveOneToOne / (totalActiveOneToOneCreators || 1)).toFixed(1)
      ),

      // earnings
      totalEarnings: formatIndianCurrencyToReadableWords(totalEarnings),
      totalEarnedCoaches: formatNumberToReadable(totalEarnedCoaches),
      avgEarningsForEarnedCoach: formatIndianCurrencyToReadableWords(
        (totalEarnings / (totalEarnedCoaches || 1)).toFixed(1)
      ),

      // customers
      totalCustomers: formatNumberToReadable(totalCustomers),
      totalFreeCustomers: formatNumberToReadable(totalFreeCustomers),
      totalPaidCustomers: formatNumberToReadable(
        totalCustomers - totalFreeCustomers
      ),
    };
  }, [dashboardMetrics]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ alignItems: "stretch" }}>
      {/* Top Row */}
      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #1890ff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Total Coaches
            </Text>

            <TeamOutlined style={{ fontSize: 16, color: "#1890ff" }} />
          </div>
          <Statistic
            value={formattedDashboardMetrics.totalCoaches}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formattedDashboardMetrics.totalAssignedCoaches} Assigned |{" "}
            {formattedDashboardMetrics.totalUnassignedCoaches} Unassigned
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #52c41a",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Active Coaches
            </Text>

            <TeamOutlined style={{ fontSize: 16, color: "#52c41a" }} />
          </div>
          <Statistic
            value={formattedDashboardMetrics.totalActiveCoaches}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formattedDashboardMetrics.totalInactiveCoaches} Inactive (last 3
            months earnings ={" "}
            {formattedDashboardMetrics.totalActiveCoachesEarnings})
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #1890ff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Setup Distribution
            </Text>
            <TeamOutlined style={{ fontSize: 16, color: "#1890ff" }} />
          </div>
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text strong style={{ fontSize: 16, color: "#262626" }}>
              F: {formattedDashboardMetrics.totalFreedomCoaches}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              B:
              {formattedDashboardMetrics.freedomCoaches.tier1}
              {"  "}
              P:
              {formattedDashboardMetrics.freedomCoaches.tier2}
              {"  "}
              A:
              {formattedDashboardMetrics.freedomCoaches.tier3}
              {"  "}
              U:
              {formattedDashboardMetrics.freedomCoaches.tier4}
            </Text>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text strong style={{ fontSize: 16, color: "#262626" }}>
              E:{"  "}
              {formattedDashboardMetrics.totalEnterpriseCoaches}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              B:
              {formattedDashboardMetrics.enterpriseCoaches.tier1}
              {"  "}
              P:
              {formattedDashboardMetrics.enterpriseCoaches.tier2}
              {"  "}
              A:
              {formattedDashboardMetrics.enterpriseCoaches.tier3}
              {"  "}
              U:
              {formattedDashboardMetrics.enterpriseCoaches.tier4}
            </Text>
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #722ed1",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Courses
            </Text>
            <BookOutlined style={{ fontSize: 16, color: "#722ed1" }} />
          </div>
          <Statistic
            value={dashboardMetrics.totalActiveCourses || 0}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            created by {formattedDashboardMetrics.totalActiveCourseCreators} out
            of {formattedDashboardMetrics.totalCoaches} | avg{" "}
            {formattedDashboardMetrics.avgActiveCoursesForCreator}
          </Text>
        </Card>
      </Col>

      {/* Bottom Row */}
      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #fa8c16",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Workshops
            </Text>
            <VideoCameraOutlined style={{ fontSize: 16, color: "#fa8c16" }} />
          </div>
          <Statistic
            value={formattedDashboardMetrics.totalActiveWorkshops}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            created by {formattedDashboardMetrics.totalActiveWorkshopCreators}{" "}
            out of {formattedDashboardMetrics.totalCoaches} | avg{" "}
            {formattedDashboardMetrics.avgActiveWorkshopsForCreator}
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #1890ff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              One-on-Ones
            </Text>
            <MessageOutlined style={{ fontSize: 16, color: "#1890ff" }} />
          </div>
          <Statistic
            value={formattedDashboardMetrics.totalActiveOneToOne}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            created by {formattedDashboardMetrics.totalActiveOneToOneCreators}{" "}
            out of {formattedDashboardMetrics.totalCoaches} | avg{" "}
            {formattedDashboardMetrics.avgActiveOneToOneForCreator}
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #52c41a",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Total Earnings
            </Text>
            <DollarOutlined style={{ fontSize: 16, color: "#52c41a" }} />
          </div>
          <Statistic
            value={formattedDashboardMetrics.totalEarnings}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            by {formattedDashboardMetrics.totalEarnedCoaches} out of{" "}
            {formattedDashboardMetrics.totalCoaches} | avg{" "}
            {formattedDashboardMetrics.avgEarningsForEarnedCoach}
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #fa8c16",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            ...cardStyle,
          }}
          bodyStyle={bodyStyle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Total Customers
            </Text>
            <TeamOutlined style={{ fontSize: 16, color: "#fa8c16" }} />
          </div>
          <Statistic
            value={formattedDashboardMetrics.totalCustomers}
            valueStyle={{
              fontSize: 18,
              fontWeight: "bold",
              color: "#262626",
            }}
          />
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formattedDashboardMetrics.totalFreeCustomers} Free |{" "}
            {formattedDashboardMetrics.totalPaidCustomers} Paid
          </Text>
        </Card>
      </Col>
    </Row>
  );
};

export default DashboardCards;
